import React, { PureComponent } from 'react';
import {
    Text,
    TouchableOpacity,
    View,
    SectionList,
    Platform,
    Image,
    ScrollView
} from 'react-native-web';
import PropTypes from 'prop-types';
import * as Animatable from 'react-native-animatable';
import moment from 'moment';
import { toast } from 'react-hot-toast';

// Child Components
import ModifierGroup from './component_modifier_group';

// Custom Components
import LoadingView from '../../components/component_loading_view';
import { Button, InScreenModal, NavBar } from '../../components';

// APIs
import { createFoodModifier, getFoodItems } from '../../modules';

// Utils
import { showAlert, showAppError, toTitleCase } from '../../utils/util_helpers';

// Configs
import GLOBAL from '../../configs/config_global';
import { getGlobalState, STATE_KEYS } from '../../configs/config_state';

export default class ModifierCopyModal extends PureComponent {
    // region Props
    static propTypes = {
        foodItemData: PropTypes.object,
        modifierGroupNameList: PropTypes.array,
        onModifierGroupsImported: PropTypes.func,
        categoryData: PropTypes.object,
        truckData: PropTypes.object
    };

    static defaultProps = {
        foodItemData: {},
        modifierGroupNameList: [],
        onModifierGroupsImported: () => {},
        categoryData: {},
        truckData: {}
    };
    // endregion

    // region Constructor
    state = {
        startLoadingComp: false,
        isLoading: false,
        showModifierCopyModal: false,
        isLoadingModifiers: true,
        categoryList: {},
        selectedFoodItem: null,
        tabIndex: 0,
        selectedModifiers: [],
        isLoadingImport: false,
        currentPage: 0
    };
    // endregion

    // region Function - Show Modal
    showModal() {
        this.setState({ showModifierCopyModal: true, isLoadingModifiers: true }, () => {
            this.goFetchModifierList();
        });
    }

    closeModal() {
        this.setState({ currentPage: 0 }) ;
        this.setState({ showModifierCopyModal: false, selectedModifiers: [] });
    }
    // endregion

    // region Function - Go Fetch Modifier List
    goFetchModifierList() {
        this.setState({ isLoadingModifiers: true });
        const menuData = getGlobalState(STATE_KEYS.MENU_DATA);
        Promise.all(menuData.map((category) => this.getCategoryFoodList(category.id)))
            .then((resultArr) => {
                const categoryList = {};
                menuData.forEach((category, index) => {
                    const modifierList = resultArr[index].filter((foodItem) => (
                        (foodItem.id !== this.props.foodItemData?.id) &&
                        foodItem['modifiers']?.length > 0
                    ));
                    if (modifierList.length > 0) {
                        categoryList[category.id] = category;
                        categoryList[category.id]['foodItems'] = modifierList;
                    }
                });

                console.log('[SUCCESS] FINAL CATEGORY LIST', categoryList);

                this.setState({ isLoadingModifiers: false, categoryList });
            })
            .catch((error) => {
                console.log('[FAILED] GET ALL MODIFIERS', error);
                this.setState({
                    isLoadingModifiers: true,
                    categoryList: {}
                });
            });
    }

    // region Function - Fetch Food List Data
    getAllCategoryFood(firstResult, categoryID) {
        return new Promise((resolve) => {
            if (firstResult.lastPage > 1) {
                let pageArr = [];
                for (let i = 2; i <= firstResult.lastPage; ++i) pageArr = pageArr.concat(i);

                Promise.all(pageArr.map((pageCount) => getFoodItems(this.props.truckData.id, categoryID, pageCount).catch(() => Promise.resolve([]))))
                    .then((allResult) => {
                        let finalData = firstResult.data;
                        allResult.forEach((smallResult) => finalData = finalData.concat(smallResult.data));
                        resolve(finalData);
                    })
                    .catch((error) => {
                        console.log('[FAILED] getAllFoodData (modifierCopyModal)', error);
                        resolve([]);
                    });
            }
            else resolve(firstResult.data);
        });
    }

    getCategoryFoodList(categoryID) {
        return new Promise((resolve) => {
            getFoodItems(this.props.truckData.id, categoryID, 1)
                .then((result) => {
                    this.getAllCategoryFood(result, categoryID)
                        .then((categoryFoodList) => resolve(categoryFoodList));
                })
                .catch((error) => {
                    console.log('[FAILED] getCategoryFoodList', error);
                    resolve([]);
                });
        });
    }
    // endregion

    // endregion

    // region Render Section Header
    renderSectionHeader(text) {
        return(
            <View style={{ width: '100%' }}>
                <View style={{ width: '100%', height: 10, backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY }}/>

                {/* Section Header */}
                <View style={{ width: '100%', paddingVertical: 14, paddingHorizontal: 16, backgroundColor: 'white' }}>
                    <Text style={{ color: 'black', fontSize: 28, letterSpacing: -0.2, backgroundColor: 'transparent', fontWeight: 'bold' }}>
                        {toTitleCase(text)}
                    </Text>
                </View>

                <View style={{ width: '100%', height: 1, backgroundColor: 'rgba(0, 0, 0, 0.05)' }}/>
            </View>
        );
    }
    // endregion

    // region Render Section Item
    renderSectionItem(item) {
        const modifierCount = item.modifiers.length;

        return(
            <TouchableOpacity
                onPress={() => this.setState({ selectedFoodItem: item }, () => this.setState({ currentPage: 1 }) )}
                style={{ alignSelf: 'stretch', padding: 15, backgroundColor: 'white', flexDirection: 'row', alignItems: 'center' }}>
                {/* Photo */}
                <Image
                    source={item.photo ? { uri: item.photo } : require('../../images/illustration-empty-food.png')}
                    style={{ width: 120, height: 120 }}
                    resizeMode={'cover'}
                />

                {/* Info Area */}
                <View style={[{ flex: 1, alignSelf: 'stretch', marginLeft: 16, marginRight: 8 }, Platform.OS === 'android' && { marginHorizontal: 10 }]}>
                    {/* Food Name */}
                    <Text style={{ fontWeight: '500', fontSize: 18, width: '100%', color: GLOBAL.COLOR.BASIC.BLACK_TWO }}>
                        {item.name || ''}
                    </Text>

                    {/* Food Description */}
                    <Text style={{ color: GLOBAL.COLOR.TEXT_GRAY, marginTop: 10, fontSize: 16 }} numberOfLines={1}>
                        {item.description || ''}
                    </Text>

                    <View style={{ flex: 1 }}/>

                    {/* Modifier Count */}
                    <View style={{ paddingHorizontal: 8, paddingVertical: 6, borderRadius: 4, borderWidth: 2, borderColor: GLOBAL.COLOR.DIVIDER, alignSelf: 'flex-start' }}>
                        <Text style={{ color: GLOBAL.COLOR.BASIC.BLACK_TWO, fontWeight: '500', fontSize: 16 }}>
                            {modifierCount} modifier group{modifierCount > 1 ? 's' : ''}
                        </Text>
                    </View>
                </View>

                {/* Divider */}
                <View style={{ position: 'absolute', left: 8, right: 8, bottom: 0, height: 1, marginHorizontal: 8, backgroundColor: 'rgba(0, 0, 0, 0.05)' }}/>
            </TouchableOpacity>
        );
    }
    // endregion

    // region Render Category List
    renderCategoryList() {
        return(
            <Animatable.View
                key={'categoryList'}
                animation={'fadeInLeft'}
                style={{ flex: 1, width: '100%' }}>
                {/* Description */}
                <View style={{ width: '100%', backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY, alignItems: 'center', justifyContent: 'center', padding: 16, paddingBottom: 6 }}>
                    <Text style={{ textAlign: 'center', color: GLOBAL.COLOR.BASIC.GRAY, lineHeight: 28 }}>
                        Food items that have modifiers are shown below,
                        {'\n'}tap an item to get started
                    </Text>
                </View>

                {/* Category List */}
                <SectionList
                    style={{ flex: 1, width: '100%', backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY }}
                    showsVerticalScrollIndicator={false}
                    contentContainerStyle={{ paddingBottom: 24 }}
                    sections={Object.keys(this.state.categoryList).map((categoryID) => ({
                        title: this.state.categoryList[categoryID].name,
                        data: this.state.categoryList[categoryID]['foodItems']
                    }))}
                    renderItem={({ item }) => this.renderSectionItem(item)}
                    renderSectionHeader={({ section }) => this.renderSectionHeader(section.title)}
                    keyExtractor={(item, index) => index}
                />
            </Animatable.View>
        );
    }
    // endregion

    // region Render Modifier Selector View
    renderModifierSelectorView() {
        const selectedGroupCount = this.state.selectedModifiers.length;
        return(
            <Animatable.View
                key={'modifierSelector'}
                animation={'fadeInRight'}
                style={{ flex: 1, width: '100%', backgroundColor: 'white' }}>
                {
                    this.state.selectedFoodItem &&
                    <View style={{ flex: 1, width: '100%' }}>
                        <ScrollView
                            style={{ flex: 1, width: '100%' }}
                            contentContainerStyle={{ paddingBottom: 24 }}
                            showsVerticalScrollIndicator={false}>
                            {
                                (this.state.selectedFoodItem['modifiers'] || []).map((modifierGroup, index) => {
                                    const isSelected = this.state.selectedModifiers.findIndex((item) => item.id === modifierGroup.id) > -1;
                                    const isLast = index === (this.state.selectedFoodItem['modifiers']?.length - 1);
                                    return(
                                        <View key={modifierGroup?.id || index} style={{ flexDirection: 'row', alignItems: 'flex-start', alignSelf: 'stretch', padding: 24, borderBottomWidth: isLast ? 0 : 1, borderColor: GLOBAL.COLOR.DIVIDER }}>
                                            {/* Modifier Group Data */}
                                            <ModifierGroup
                                                modifierGroupData={modifierGroup}
                                                modifierGroupIndex={index}
                                                customStyle={{ flex: 1, marginTop: 0 }}
                                                onModifierGroupPressed={() => {
                                                    if (isSelected) {
                                                        this.setState({ selectedModifiers: this.state.selectedModifiers.filter((item) => item.id !== modifierGroup.id) });
                                                    }
                                                    else {
                                                        if (this.props.modifierGroupNameList.includes(modifierGroup.name.trim().toLowerCase())) {
                                                            showAlert({ text: 'This modifier group name already exists in your food item, this group cannot be selected' });
                                                            return;
                                                        }

                                                        this.setState({ selectedModifiers: this.state.selectedModifiers.concat(modifierGroup) });
                                                    }
                                                }}
                                            />

                                            {/* Select Indicator */}
                                            <View style={{
                                                width: 24, height: 24, marginLeft: 16,
                                                borderRadius: 16, borderWidth: 2, borderColor: isSelected ? GLOBAL.COLOR.MAIN : GLOBAL.COLOR.TEXT_LIGHT_GRAY,
                                                backgroundColor: isSelected ? GLOBAL.COLOR.MAIN : 'white',
                                                alignItems: 'center', justifyContent: 'center'
                                            }}>
                                                {
                                                    isSelected &&
                                                    <View style={{ width: 8, height: 8, borderRadius: 6, backgroundColor: 'white' }}/>
                                                }
                                            </View>
                                        </View>
                                    );
                                })
                            }
                        </ScrollView>

                        {/* Cover Loading View */}
                        {this.state.isLoadingImport && <View style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, backgroundColor: 'rgba(255, 255, 255, 0.5)' }}/>}
                    </View>
                }

                {/* Import Button */}
                {
                    selectedGroupCount > 0 &&
                    <Button
                        onPress={() => {
                            const usingModifierList = this.state.selectedModifiers.map((item, index) => Object.assign({}, item, { id: `modifierGroup_${moment().unix()}_${index}` }));
                            if (!this.props.foodItemData?.id) {
                                this.props.onModifierGroupsImported(usingModifierList);
                            }
                            else {
                                showAlert({
                                    text: `Confirm importing ${usingModifierList.length} modifier group${usingModifierList.length > 1 ? 's' : ''} to the food item <p style="color: blue; font-weight: bold;">${this.props.foodItemData.name}</p>`,
                                    cancelButton: { text: 'Cancel' },
                                    confirmButton: {
                                        text: 'Confirm',
                                        color: GLOBAL.COLOR.MAIN,
                                        onPress: () => {
                                            this.setState({ isLoadingImport: true });
                                            console.log('selectedModifiers', this.state.selectedModifiers);
                                            setTimeout(() => {
                                                const apiActions = usingModifierList.map((modifierGroup, index) => new Promise((resolve) => {
                                                    setTimeout(() => {
                                                        createFoodModifier(this.props.truckData.id, this.props.categoryData.id, this.props.foodItemData.id, modifierGroup)
                                                            .then((result) => resolve(result['modifiers'].pop()))
                                                            .catch(() => resolve(null))
                                                    }, index * 2000);
                                                }));

                                                // Call API
                                                Promise.all(apiActions)
                                                    .then((resultArr) => {
                                                        this.setState({ isLoadingImport: false });
                                                        this.props.onModifierGroupsImported(resultArr);
                                                        setTimeout(() => toast(`Successfully imported ${usingModifierList.length} modifier group(s) to your food item`), 500);
                                                    })
                                                    .catch((error) => {
                                                        this.setState({ isLoadingImport: false });
                                                        showAppError(error);
                                                    });
                                            }, 2000);
                                        }
                                    }
                                });
                            }
                        }}
                        style={{ backgroundColor: GLOBAL.COLOR.MAIN, marginHorizontal: 16, marginBottom: 24 }}
                        text={'Copy Selected Group' + (selectedGroupCount > 1 ? 's' : '') + ` (${selectedGroupCount})`}
                        textStyle={{ color: 'white' }}
                        loadingText={'Importing modifier groups'}
                        textColor={'white'}
                        isLoading={this.state.isLoadingImport}
                    />
                }
            </Animatable.View>
        );
    }
    // endregion

    // region MAIN RENDER FUNCTION
    render() {
        return(
            <InScreenModal visible={this.state.showModifierCopyModal}>
                {/* Nav Bar */}
                <NavBar
                    title={'Copy Modifier Data'}
                    subTitle={this.props.foodItemData ? `for "${this.props.foodItemData.name}"` : 'for your new food item'}
                    ignoreTopBarHeight={true}
                    leftButtonIconName={(this.state.currentPage === 0) ? 'close' : 'keyboard-arrow-left'}
                    iconSize={(this.state.currentPage === 0) ? 32 : 40}
                    horizontalSpacing={16}
                    leftButtonOnPress={() => {
                        if (this.state.currentPage === 0) {
                            this.closeModal();
                        }
                        else this.setState({ currentPage: 0 }) ;
                    }}
                />

                {/* Main Content */}
                <View style={{ flex: 1, width: '100%', backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY }}>
                    {/* Cover Loading View */}
                    {
                        this.state.isLoadingModifiers ?
                            <LoadingView isAbsolute={true} description={'Loading data'}/>
                            :
                            <View style={{ flex: 1, width: '100%', backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY }}>
                                {
                                    this.state.currentPage === 0 ?
                                        this.renderCategoryList()
                                        :
                                        this.renderModifierSelectorView()
                                }
                            </View>
                    }
                </View>
            </InScreenModal>
        );
    }
    // endregion
}
